import { useEffect, useRef, useState } from 'react';
import { MetaRow } from '@/ui';
import { StudioButton as Button } from './styles';
import type { CollaborationSelection, StudioResource } from './contracts';
import { LiveClient, type LiveState } from './liveClient';
import { position, scalarOffset, utf16Offset } from './textOperation';
import { Field, Note, Panel, Row } from './styles';
import { LiveCursors } from './LiveCursors';
import { LiveWriting } from './styles';
export function LiveEditor({
  owner,
  resource,
  onChoose,
}: {
  owner: string;
  resource: StudioResource;
  onChoose: (v: CollaborationSelection) => void;
}) {
  const [view, setView] = useState<LiveState>({
    body: '',
    version: 0,
    revision: 0,
    pending: false,
    ready: false,
    error: '',
    peers: [],
  });
  const client = useRef<LiveClient | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  useEffect(() => {
    const connection = new LiveClient(owner, resource.slug, (state, remote) => {
      const el = input.current;
      const selection =
        el && remote
          ? {
              start: position(remote, scalarOffset(el.value, el.selectionStart)),
              end: position(remote, scalarOffset(el.value, el.selectionEnd)),
            }
          : null;
      setView(state);
      if (selection)
        requestAnimationFrame(() => {
          if (el?.isConnected)
            el.setSelectionRange(
              utf16Offset(state.body, selection.start),
              utf16Offset(state.body, selection.end),
            );
        });
    });
    client.current = connection;
    void connection.start();
    return () => {
      connection.stop();
      client.current = null;
    };
  }, [owner, resource.slug]);
  const canUse =
    resource.role === 'owner' &&
    view.ready &&
    !view.pending &&
    !view.error &&
    view.body.trim() &&
    view.revision === resource.revision &&
    !resource.data.collaborators?.some((p) => p.state === 'pending');
  return (
    <Panel>
      <Row>
        <strong>Live private draft</strong>
        <Note role="status">
          {view.pending ? 'Saving changes…' : view.ready ? 'All changes saved' : 'Connecting…'}
        </Note>
      </Row>
      <Note>
        Co-authors edit together. Only the owner can publish. Later published changes use proposals
        and owner approval.
      </Note>
      <Field>
        Shared writing
        <LiveWriting>
          <textarea
            aria-label="Shared writing"
            ref={input}
            value={view.body}
            disabled={!view.ready}
            rows={8}
            onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
            onChange={(e) => client.current?.edit(e.target.value)}
            onSelect={(e) => {
              const el = e.currentTarget;
              client.current?.presence(
                scalarOffset(el.value, el.selectionStart),
                scalarOffset(el.value, el.selectionEnd),
              );
            }}
          />
          {!view.pending && (
            <LiveCursors
              body={view.body}
              peers={view.peers}
              version={view.version}
              scrollTop={scrollTop}
            />
          )}
        </LiveWriting>
      </Field>
      <Note>{Array.from(view.body).length}/5,000</Note>
      {view.peers.map((p) => (
        <Note key={p.clientId}>
          <MetaRow>
            {`${p.person.name} is here`}
            {p.version === view.version && p.head != null
              ? `cursor at character ${p.head + 1}`
              : ''}
          </MetaRow>
        </Note>
      ))}
      {view.error && <p role="alert">{view.error}</p>}
      <Row>
        <Button onClick={() => void client.current?.sync()}>Sync now</Button>
        {resource.role === 'owner' && (
          <Button
            variant="primary"
            disabled={!canUse}
            onClick={() =>
              onChoose({
                slug: resource.slug,
                title: resource.title,
                body: view.body,
                revision: view.revision,
                version: view.version,
              })
            }
          >
            Use synchronized draft
          </Button>
        )}
      </Row>
      {view.revision !== resource.revision && (
        <Note>
          Refresh the draft’s details below after editing, then review it for publication.
        </Note>
      )}
    </Panel>
  );
}
